import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Inbox, 
  Search, 
  Mail,
  MailOpen,
  Trash2,
  RefreshCw,
  Reply,
  User,
  Calendar,
  Shield,
  Eye,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Audio notification
const playNotificationSound = () => {
  try {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2LkpWQh3x1eYSQm5+bloyEfXt/ipWeoJuTioR/gYqVoKOgmI+IgoKIk56io5+XjoaCgoqVoKSknpiPh4KCipWgo6KdlY2GgYOKlqGko5+Xj4eCgoqWoaSjn5ePhoKDi5eho6Ogl5CHg4OLl6KkpKCYkIeDg4uYoqSkoJmRh4OEjJmjpaWgmpKIhISMmaSlpaGbk4iFhY2apaalop2Ui4WFjpumpaWinpWMhoaOnKempqOfloyHh4+dp6eno6CXjYiHkJ6oqKekoZiOiIiRn6ioqKWimY+JiZKgqamppqOakImKk6GqqqqnpJuRioqUoqqqq6illpKLi5Wjq6urrKaXk4yMlqSsrK2sp5iUjY2Xpa2tr62pmZWOjpimsK+wr6qal46OmbGwr7Cuq5uXj4+asbGwsK+snJiQkJuysrGxsK2dmZGRnLOzsrKxrp6akpKdtLSzs7KvoJuTk561tbS0s7ChnJSUnra2tbW0saKdlZWfuLe2trWyo56Wlp+4uLe3trOjoJeXoLm5uLi3tKShl5ihuLm5ubm1pqKYmaK6urq6ubaoo5qao7u7u7u7t6qkm5uju7y8vLy4q6WcnKS8vb29vbmtpp2dpb2+vr6+uq+on56mv7+/v7+7sKmfoKfAwMDAwLyxqqCgqMHBwcHBvbKroaGpwsLCwsK+s6yioqrDw8PDw7+0raOjq8TExMTEwLa1pKSszMXFxcXBt7alpavFxsbGxsK4t6amrcfHx8fHw7m4p6euyMjIyMjEurmpqK/JycnJycW7uqqqsMrKysrKxry7q6uxysrLy8vHvbyrrLHLzMzMzMi+va2tss3Nzc3NycC+rq6zz8/Pz8/KwcCvsLTQ0NDQ0MvCwbCwtdHR0dHRzMPCsbG21tLS0tLNxMOys7fT09PT08/GxLO0uNTU1NTU0MfFtLW51dXV1dXRyMa1trra1tbW1tLKx7e3u9vX19fX08vIuLi83NjY2NjU0NjJubm93dnZ2dnV0drKuru/3tra2trW0dvLvLzA3tvb29vX1N3MvL3B39zc3Nza1d7NvcDD4N3d3d3a1d/Ovr/E4d7e3t7c1+HQv8DG4t/f39/d2OPSwcHH4+Dg4ODe2eXTwsPJ5OHh4eHf2ufUxMTL5eLi4uLg2+nVxcXN5uPj4+Ph3OzXxsfP6OXl5eXi3u7Yx8jQ6ebm5ubk3/DayMnS6ufn5+fl4PLbycvU6+jo6Ojm4fTcy8vV7Onp6enp4vXezczX7erq6urs5Pfgzs3Z7+vr6+vt5fniztDa8Ozs7Ozu5vrkz9Hc8e3t7e3v6PsA');
    audio.volume = 0.5;
    audio.play().catch(() => {}); // Ignore autoplay errors
  } catch (e) {
    // Ignore audio errors
  }
};

export const InboxPage = () => {
  const { isAdmin, isManager, hasInboxAccess } = useAuth();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastEmailCount, setLastEmailCount] = useState(0);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const intervalRef = useRef(null);

  const fetchEmails = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/inbox`);
      const newEmails = response.data;
      
      // Check for new emails (only after initial load)
      if (lastEmailCount > 0 && newEmails.length > lastEmailCount) {
        const newCount = newEmails.length - lastEmailCount;
        toast.success(`${newCount} new email${newCount > 1 ? 's' : ''} received!`, {
          icon: <Bell className="w-4 h-4" />,
          duration: 5000,
        });
        playNotificationSound();
        
        // Request browser notification permission and show notification
        if (Notification.permission === 'granted') {
          new Notification('New Email Received', {
            body: `You have ${newCount} new email${newCount > 1 ? 's' : ''} in your inbox`,
            icon: '/favicon.ico'
          });
        } else if (Notification.permission !== 'denied') {
          Notification.requestPermission();
        }
      }
      
      setEmails(newEmails);
      setLastEmailCount(newEmails.length);
    } catch (error) {
      console.error('Failed to fetch emails:', error);
      if (showLoading) toast.error('Failed to load inbox');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [lastEmailCount]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/inbox/stats/unread`);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, []);

  useEffect(() => {
    if (hasInboxAccess()) {
      fetchEmails(true);
      fetchUnreadCount();
      
      // Request notification permission on page load
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (hasInboxAccess() && autoRefreshEnabled) {
      intervalRef.current = setInterval(() => {
        fetchEmails(false);
        fetchUnreadCount();
      }, 30000); // 30 seconds

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [autoRefreshEnabled, fetchEmails, fetchUnreadCount, hasInboxAccess]);

  const openEmail = async (email) => {
    setSelectedEmail(email);
    setDetailsOpen(true);
    
    // Mark as read if unread
    if (!email.is_read) {
      try {
        await axios.patch(`${API_URL}/inbox/${email.id}/read`);
        setEmails(emails.map(e => e.id === email.id ? { ...e, is_read: true } : e));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const handleDelete = async (emailId) => {
    if (!window.confirm('Are you sure you want to delete this email?')) return;

    try {
      await axios.delete(`${API_URL}/inbox/${emailId}`);
      toast.success('Email deleted');
      setEmails(emails.filter(e => e.id !== emailId));
      setDetailsOpen(false);
    } catch (error) {
      console.error('Failed to delete email:', error);
      toast.error('Failed to delete email');
    }
  };

  const filteredEmails = emails.filter(email => {
    const term = searchTerm.toLowerCase();
    return (
      email.from_email.toLowerCase().includes(term) ||
      (email.from_name && email.from_name.toLowerCase().includes(term)) ||
      email.subject.toLowerCase().includes(term) ||
      (email.text_body && email.text_body.toLowerCase().includes(term))
    );
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const formatFullDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!hasInboxAccess()) {
    return (
      <div className="text-center py-12">
        <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
        <p className="text-slate-500">You don't have permission to view the inbox.</p>
      </div>
    );
  }

  return (
    <div data-testid="inbox-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-900">
              Inbox
            </h1>
            {unreadCount > 0 && (
              <span className="px-2.5 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700">
                {unreadCount} new
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-slate-500">Customer replies and incoming emails</p>
            {autoRefreshEnabled && (
              <span className="flex items-center gap-1 text-xs text-emerald-600">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                Auto-refresh ON
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4 sm:mt-0">
          <Button 
            variant={autoRefreshEnabled ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
            data-testid="auto-refresh-toggle"
          >
            <Bell className={`w-4 h-4 mr-1 ${autoRefreshEnabled ? 'text-white' : ''}`} />
            {autoRefreshEnabled ? 'Auto ON' : 'Auto OFF'}
          </Button>
          <Button variant="outline" onClick={() => { fetchEmails(true); fetchUnreadCount(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Received</p>
                <p className="text-2xl font-bold text-slate-900">{emails.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Inbox className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Unread</p>
                <p className="text-2xl font-bold text-slate-900">{unreadCount}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Replies</p>
                <p className="text-2xl font-bold text-slate-900">{emails.filter(e => e.is_reply).length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Reply className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Search by sender, subject, or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12"
            data-testid="inbox-search"
          />
        </div>
      </div>

      {/* Email List */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Inbox className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {searchTerm ? 'No matching emails found' : 'No emails received yet'}
              </h3>
              <p className="text-slate-500 max-w-sm mx-auto">
                {searchTerm 
                  ? 'Try adjusting your search'
                  : 'Customer replies and incoming emails will appear here'
                }
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredEmails.map((email) => (
                <div
                  key={email.id}
                  onClick={() => openEmail(email)}
                  className={`flex items-start gap-4 p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                    !email.is_read ? 'bg-indigo-50/50' : ''
                  }`}
                  data-testid={`inbox-email-${email.id}`}
                >
                  {/* Avatar */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    !email.is_read ? 'bg-indigo-100' : 'bg-slate-100'
                  }`}>
                    {!email.is_read ? (
                      <Mail className={`w-5 h-5 ${!email.is_read ? 'text-indigo-600' : 'text-slate-400'}`} />
                    ) : (
                      <MailOpen className="w-5 h-5 text-slate-400" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium truncate ${!email.is_read ? 'text-slate-900' : 'text-slate-600'}`}>
                          {email.from_name || email.from_email}
                        </span>
                        {email.is_reply && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            Reply
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-slate-400 flex-shrink-0">
                        {formatDate(email.received_at)}
                      </span>
                    </div>
                    <p className={`text-sm truncate mb-1 ${!email.is_read ? 'font-medium text-slate-800' : 'text-slate-600'}`}>
                      {email.subject}
                    </p>
                    <p className="text-sm text-slate-400 truncate">
                      {email.text_body?.substring(0, 100) || '(No preview available)'}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEmail(email);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {isAdmin() && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(email.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Detail Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              Email Details
            </DialogTitle>
          </DialogHeader>
          {selectedEmail && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">
                        {selectedEmail.from_name || 'Unknown Sender'}
                      </p>
                      <p className="text-sm text-slate-500">{selectedEmail.from_email}</p>
                    </div>
                  </div>
                  {selectedEmail.is_reply && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700">
                      Customer Reply
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-200">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">To</p>
                    <p className="text-sm text-slate-700">{selectedEmail.to_email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Received</p>
                    <p className="text-sm text-slate-700 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatFullDate(selectedEmail.received_at)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Subject */}
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Subject</p>
                <p className="text-lg font-semibold text-slate-900">{selectedEmail.subject}</p>
              </div>

              {/* Attachments */}
              {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">
                    Attachments ({selectedEmail.attachments.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedEmail.attachments.map((attachment, index) => {
                      const isTooLarge = attachment.too_large || !attachment.content_base64;
                      return (
                        <div
                          key={index}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                            isTooLarge 
                              ? 'bg-amber-50 border border-amber-200 cursor-not-allowed' 
                              : 'bg-slate-100 hover:bg-slate-200 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (isTooLarge) {
                              toast.error(`Attachment "${attachment.filename}" was too large to store (${Math.round(attachment.size / 1024 / 1024 * 10) / 10}MB). Max size is 5MB.`);
                              return;
                            }
                            // Download with auth token
                            const token = localStorage.getItem('token');
                            fetch(`${API_URL}/inbox/${selectedEmail.id}/attachment/${index}`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            })
                            .then(res => {
                              if (!res.ok) throw new Error('Download failed');
                              return res.blob();
                            })
                            .then(blob => {
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = attachment.filename;
                              a.click();
                              window.URL.revokeObjectURL(url);
                            })
                            .catch(() => toast.error('Failed to download attachment'));
                          }}
                        >
                          <svg className={`w-4 h-4 ${isTooLarge ? 'text-amber-500' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          <span className={`text-sm font-medium ${isTooLarge ? 'text-amber-700' : 'text-slate-700'}`}>
                            {attachment.filename}
                          </span>
                          <span className={`text-xs ${isTooLarge ? 'text-amber-500' : 'text-slate-400'}`}>
                            ({attachment.size >= 1024 * 1024 
                              ? `${Math.round(attachment.size / 1024 / 1024 * 10) / 10}MB` 
                              : `${Math.round(attachment.size / 1024)}KB`})
                          </span>
                          {isTooLarge && (
                            <span className="text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">
                              Too large
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Body */}
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Message</p>
                {selectedEmail.html_body ? (
                  <div className="border rounded-lg bg-white overflow-hidden">
                    <iframe
                      srcDoc={`
                        <!DOCTYPE html>
                        <html>
                        <head>
                          <meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data: https:; font-src https:;">
                          <style>
                            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 16px; color: #333; line-height: 1.6; }
                            img { max-width: 100%; height: auto; }
                            a { color: #4338ca; pointer-events: none; text-decoration: underline; }
                            blockquote { margin: 10px 0; padding-left: 15px; border-left: 3px solid #ddd; color: #666; }
                            pre { background: #f5f5f5; padding: 10px; overflow-x: auto; }
                            script, iframe, object, embed { display: none !important; }
                          </style>
                        </head>
                        <body>${selectedEmail.html_body}</body>
                        </html>
                      `}
                      title="Email Content"
                      className="w-full border-0"
                      style={{ minHeight: '400px', height: 'auto' }}
                      sandbox=""
                      onLoad={(e) => {
                        // Auto-resize iframe to content
                        try {
                          const iframe = e.target;
                          const height = iframe.contentWindow.document.body.scrollHeight;
                          iframe.style.height = Math.min(Math.max(height + 40, 200), 600) + 'px';
                        } catch(err) {}
                      }}
                    />
                  </div>
                ) : selectedEmail.text_body ? (
                  <div className="border rounded-lg bg-white p-4 whitespace-pre-wrap text-slate-700 max-h-[400px] overflow-y-auto">
                    {selectedEmail.text_body}
                  </div>
                ) : (
                  <div className="border rounded-lg bg-slate-50 p-4 text-slate-500 text-center">
                    (No content available)
                  </div>
                )}
              </div>

              {/* Actions */}
              {isAdmin() && (
                <div className="flex justify-end pt-4 border-t">
                  <Button
                    variant="destructive"
                    onClick={() => handleDelete(selectedEmail.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Email
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InboxPage;
